<template>
  <canvas ref="canvas"/>
</template>

<script>
import Chart from 'chart.js/auto'

export default {
  name: 'NetDistributionChart',
  props: {
    customers: {
      type: Array,
      required: true
    },
    showWithClipping: {
      type: Boolean,
      default: true
    }
  },
  data() {
    return {
      chart: null
    }
  },
  mounted() {
    this.createChart()
  },
  watch: {
    customers: {
      handler() {
        this.updateChart()
      },
      deep: true
    },
    showWithClipping() {
      this.updateChart()
    }
  },
  beforeDestroy() {
    if (this.chart) {
      this.chart.destroy()
    }
  },
  methods: {
    createChart() {
      const ctx = this.$refs.canvas.getContext('2d')

      this.chart = new Chart(ctx, {
        type: 'bar',
        data: {
          labels: this.getBinLabels(),
          datasets: [
            {
              label: 'Number of Customers',
              data: this.getNetDistributionData(),
              backgroundColor: 'rgba(255, 99, 132, 0.6)',
              borderColor: 'rgba(255, 99, 132, 1)',
              borderWidth: 1
            }
          ]
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          plugins: {
            title: {
              display: true,
              text: 'Net Customer Value Distribution'
            },
            legend: {
              display: false,
            },
            tooltip: {
              callbacks: {
                label: (context) => {
                  const binIndex = context.dataIndex
                  const avgNet = this.netBins && this.netBins[binIndex]
                    ? this.netBins[binIndex].averageNet
                    : 0
                  const totalNet = this.netBins && this.netBins[binIndex]
                    ? this.netBins[binIndex].totalNet
                    : 0
                  const lines = [`${context.parsed.y} customers`]
                  if (avgNet > 0) {
                    lines.push(`Average: $${(avgNet / 100).toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`)
                    lines.push(`Total: $${(totalNet / 100).toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`)
                  }
                  return lines
                }
              }
            }
          },
          scales: {
            x: {
              title: {
                display: true,
                text: 'Net Value Range (thousands)'
              }
            },
            y: {
              title: {
                display: true,
                text: 'Number of Customers'
              },
              beginAtZero: true,
              ticks: {
                stepSize: 1
              }
            }
          }
        }
      })
    },

    updateChart() {
      if (this.chart) {
        this.chart.data.labels = this.getBinLabels()
        this.chart.data.datasets[0].data = this.getNetDistributionData()
        this.chart.update()
      }
    },

    getNetDistributionData() {
      const netValues = this.customers.map(customer =>
        customer.stats && typeof customer.stats.net === 'number' ? customer.stats.net / 100 / 1000 : 0 // Convert to thousands
      ).filter(net => net > 0)

      const bins = this.createBins(netValues)
      this.netBins = bins // Store for tooltip access
      return bins.map(bin => bin.count)
    },

    getBinLabels() {
      const netValues = this.customers.map(customer =>
        customer.stats && typeof customer.stats.net === 'number' ? customer.stats.net / 100 / 1000 : 0
      ).filter(net => net > 0)

      const bins = this.createBins(netValues)
      return bins.map(bin => `$${bin.min.toFixed(1)}k-${bin.max.toFixed(1)}k`)
    },

    createBins(values) {
      if (values.length === 0) return []

      const validValues = values.filter(value => value > 0)
      if (validValues.length === 0) return []

      let clippedValues = validValues

      // Remove outliers using IQR method only if clipping is enabled
      if (this.showWithClipping) {
        const sortedValues = [...validValues].sort((a, b) => a - b)
        const q1 = sortedValues[Math.floor(sortedValues.length * 0.25)]
        const q3 = sortedValues[Math.floor(sortedValues.length * 0.75)]
        const iqr = q3 - q1
        const lowerBound = q1 - 1.5 * iqr
        const upperBound = q3 + 1.5 * iqr

        clippedValues = validValues.filter(value => value >= lowerBound && value <= upperBound)
      }

      if (clippedValues.length === 0) {
        // Fallback to original data if all values are outliers
        const minValue = Math.min(...validValues)
        const maxValue = Math.max(...validValues)
        return [{
          min: minValue,
          max: maxValue,
          count: validValues.length,
          averageNet: this.calculateFallbackAverageNet()
        }]
      }

      const minValue = Math.min(...clippedValues)
      const maxValue = Math.max(...clippedValues)
      const binCount = Math.min(8, clippedValues.length) // Max 8 bins, or number of values if less

      if (minValue === maxValue) {
        return [{
          min: minValue,
          max: maxValue,
          count: clippedValues.length,
          averageNet: this.calculateFallbackAverageNet()
        }]
      }

      const binWidth = (maxValue - minValue) / binCount
      const bins = []

      for (let i = 0; i < binCount; i++) {
        const binMin = minValue + (i * binWidth)
        const binMax = minValue + ((i + 1) * binWidth)

        const valuesInBin = clippedValues.filter(value => {
          return i === binCount - 1 ?
            (value >= binMin && value <= binMax) :
            (value >= binMin && value < binMax)
        })

        const count = valuesInBin.length
        let averageNet = 0
        let totalNet = 0

        if (count > 0) {
          // Calculate average and total net for customers in this net value bin
          const customersInBin = this.customers.filter(customer => {
            if (!customer.stats || typeof customer.stats.net !== 'number' || customer.stats.net <= 0) {
              return false
            }
            
            const netInThousands = customer.stats.net / 100 / 1000
            return i === binCount - 1 ?
              (netInThousands >= binMin && netInThousands <= binMax) :
              (netInThousands >= binMin && netInThousands < binMax)
          })
          
          const netValues = customersInBin
            .filter(customer => customer.stats && typeof customer.stats.net === 'number')
            .map(customer => customer.stats.net)
          
          if (netValues.length > 0) {
            totalNet = netValues.reduce((sum, val) => sum + val, 0)
            averageNet = totalNet / netValues.length
          }
        }

        bins.push({
          min: binMin,
          max: binMax,
          count: count,
          averageNet: averageNet,
          totalNet: totalNet
        })
      }

      return bins
    },

    calculateFallbackAverageNet() {
      // Calculate overall average net value of all customers
      const netValues = this.customers
        .filter(customer => customer.stats && typeof customer.stats.net === 'number')
        .map(customer => customer.stats.net)

      return netValues.length > 0 ? netValues.reduce((sum, val) => sum + val, 0) / netValues.length : 0
    },

    calculateAverageNetForBin(binMin, binMax, isLastBin) {
      // Find customers whose net values fall within this bin range (convert back from thousands)
      const customersInBin = this.customers.filter(customer => {
        if (!customer.stats || typeof customer.stats.net !== 'number' || customer.stats.net <= 0) {
          return false
        }

        const netInThousands = customer.stats.net / 100 / 1000
        return isLastBin ?
          (netInThousands >= binMin && netInThousands <= binMax) :
          (netInThousands >= binMin && netInThousands < binMax)
      })

      if (customersInBin.length === 0) return 0

      // Calculate average net value for customers in this bin
      const netValues = customersInBin
        .filter(customer => customer.stats && typeof customer.stats.net === 'number')
        .map(customer => customer.stats.net)

      return netValues.length > 0 ? netValues.reduce((sum, val) => sum + val, 0) / netValues.length : 0
    }
  }
}
</script>

<style scoped>
</style>